import axios from 'axios';
import { getConfig, configReady } from './config';

const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000;

// ⚠️ 여기서 즉시 getConfig()로 baseURL을 뽑지 않습니다.
//    axios 인스턴스는 baseURL 없이 만들고, 요청 인터셉터에서 주입합니다.
const api = axios.create({
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  withCredentials: true,
});

// 에러 메시지 포맷팅
const formatErrorMessage = (error: any): string => {
  if (error?.response?.data?.detail) return error.response.data.detail;
  if (error?.message) return error.message;
  return '알 수 없는 오류가 발생했습니다.';
};

// 재시도 로직
const retryRequest = async (
  requestFn: () => Promise<any>,
  maxRetries = MAX_RETRIES,
  delay = INITIAL_RETRY_DELAY
) => {
  let lastError: any;
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await requestFn();
    } catch (error: any) {
      lastError = error;
      if (error?.response?.status === 401) {
        throw new Error(formatErrorMessage(error));
      }
      if (i === maxRetries - 1) break;
      await new Promise((r) => setTimeout(r, delay * Math.pow(2, i)));
    }
  }
  throw new Error(formatErrorMessage(lastError));
};

// 요청 인터셉터: 여기서 baseURL과 토큰을 안전하게 주입
api.interceptors.request.use(
  async (config) => {
    // config 준비 완료를 보장 (모듈 로드시점이 아니라 "요청 직전"에 보장)
    const { API_BASE_URL } = await configReady;

    // 요청마다 baseURL을 주입 (핫리로드/런타임주입에도 강함)
    if (API_BASE_URL) {
      config.baseURL = API_BASE_URL;
    }

    // 디버그 로그
    console.log('API 요청:', {
      url: config.url,
      method: config.method,
      headers: config.headers,
      data: config.data,
      baseURL: config.baseURL,
    });

    const token = localStorage.getItem('token');
    if (token) {
      // 타입 가드
      config.headers = config.headers ?? {};
      (config.headers as any).Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    console.error('API 요청 오류:', error);
    return Promise.reject(error);
  }
);

// 응답 인터셉터
api.interceptors.response.use(
  (response) => {
    console.log('API 응답:', {
      url: response.config.url,
      status: response.status,
      data: response.data,
    });
    return response;
  },
  (error) => {
    console.error('API 응답 오류:', {
      url: error.config?.url,
      status: error.response?.status,
      data: error.response?.data,
      message: error.message,
    });

    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    } else if (error.response?.status === 500) {
      console.error('서버 오류:', error.response.data);
    } else if (error.code === 'ECONNABORTED') {
      console.error('요청 시간 초과');
    }

    return Promise.reject(error);
  }
);

export { api as default, retryRequest, formatErrorMessage };